/**
 * Rol bazlı sayfa yetkilendirmesi.
 * ADMIN: tam yetki.
 * YONETICI: Muhasebe & Finans, Raporlar, Kullanıcılar & Yetkiler, Stok Ayarları, Genel Ayarlar hariç.
 * PERSONEL: Sadece Müşteriler, Servis Fişleri, Hizmet Kalemleri, Dış Hizmet Kalemleri, Şikayet Kalemleri (+ Dashboard).
 */

export const ROLE_ADMIN = 'ADMIN';
export const ROLE_YONETICI = 'YONETICI';
export const ROLE_PERSONEL = 'PERSONEL';

/** Personel sadece bu path'leri (ve alt path'leri) görebilir */
const PERSONEL_ALLOWED_PATH_PREFIXES = [
  '/dashboard',
  '/service-customers',
  '/service-orders',   // /service-orders, /service-orders/add, /service-orders/123 vb.
  '/service-items',
  '/external-service-items',
  '/complaint-items',
];

/** Yönetici bu path'leri ve grupları göremez */
const YONETICI_HIDDEN_KEYS = new Set([
  'cash-management',   // Muhasebe & Finans
  'reports',           // Raporlar
  '/users',            // Kullanıcılar & Yetkiler
  '/import-stock',     // İçe Aktar - sadece Admin
  '/stock-settings',   // Stok Ayarları
  '/general-sms-settings', // Genel Ayarlar
  '/deleted-records',  // Silinen Kayıtlar - sadece Admin
]);

/** Bir menü key'inin (path veya grup) Yönetici için gizli olup olmadığı */
function isHiddenForYonetici(key: string): boolean {
  if (YONETICI_HIDDEN_KEYS.has(key)) return true;
  if (key.startsWith('/reports')) return true;
  if (key.startsWith('/cash-registers') || key.startsWith('/bank-accounts')) return true;
  return false;
}

/** Yönetici için bu path erişilemez mi (Muhasebe, Raporlar, Kullanıcılar, İçe Aktar, Stok/Genel Ayarlar) */
function isPathForbiddenForYonetici(path: string): boolean {
  const p = path.replace(/\/$/, '') || '/';
  if (p === '/users' || p === '/import-stock' || p === '/stock-settings' || p === '/general-sms-settings' || p === '/deleted-records') return true;
  if (p.startsWith('/reports')) return true;
  if (p.startsWith('/cash-registers') || p.startsWith('/bank-accounts')) return true;
  return false;
}

/** Personel için bir path erişilebilir mi */
export function canPersonelAccessPath(path: string): boolean {
  const normalized = path.replace(/\/$/, '') || '/';
  return PERSONEL_ALLOWED_PATH_PREFIXES.some(prefix => normalized === prefix || normalized.startsWith(prefix + '/'));
}

/** Mevcut path kullanıcı rolüne göre erişilebilir mi */
export function canAccessPath(path: string, roleCodes: string[] = []): boolean {
  if (roleCodes.includes(ROLE_ADMIN)) return true;
  if (roleCodes.includes(ROLE_PERSONEL)) return canPersonelAccessPath(path);
  if (roleCodes.includes(ROLE_YONETICI)) return !isPathForbiddenForYonetici(path);
  return true; // rol yoksa varsayılan erişim (güvenlik backend'de)
}

/** Personel için görünür menü key'leri (sadece bu sayfalar) */
const PERSONEL_VISIBLE_KEYS = new Set([
  '/dashboard',
  'service-management',
  '/service-customers',
  '/service-orders',
  '/service-items',
  '/external-service-items',
  '/complaint-items',
]);

/** Menü öğesinin (key) bu roller için gösterilip gösterilmeyeceği */
export function isMenuItemVisibleForRoles(menuKey: string, roleCodes: string[]): boolean {
  if (roleCodes.includes(ROLE_ADMIN)) return true;
  if (roleCodes.includes(ROLE_PERSONEL)) {
    return PERSONEL_VISIBLE_KEYS.has(menuKey);
  }
  if (roleCodes.includes(ROLE_YONETICI)) {
    return !isHiddenForYonetici(menuKey);
  }
  return true;
}

/** Menü öğesi (key veya children olabilir; divider vb. için key opsiyonel). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MenuItemWithKey = any;

/** Menü öğelerini role göre filtreler (children dahil). */
export function filterMenuByRole(items: MenuItemWithKey[], roleCodes: string[]): MenuItemWithKey[] {
  if (roleCodes.includes(ROLE_ADMIN)) return items;
  const result: MenuItemWithKey[] = [];
  for (const item of items) {
    if (!item || !('key' in item) || item.key === undefined) {
      result.push(item);
      continue;
    }
    if (item.children && Array.isArray(item.children)) {
      const filteredChildren = filterMenuByRole(item.children, roleCodes);
      if (filteredChildren.length === 0 && !isMenuItemVisibleForRoles(item.key, roleCodes)) continue;
      if (!isMenuItemVisibleForRoles(item.key, roleCodes)) continue;
      result.push({ ...item, children: filteredChildren });
    } else {
      if (!isMenuItemVisibleForRoles(item.key, roleCodes)) continue;
      result.push(item);
    }
  }
  return result;
}
